import {
  AuthError,
  AuthenticationResult,
  Configuration,
  EventType,
  InteractionRequiredAuthError,
  PublicClientApplication,
  RedirectRequest,
} from '@azure/msal-browser';
import { jwtDecode } from 'jwt-decode';

import { AuthConfig, getLoginRequest } from './config';
import { CustomNavigationClient } from './CustomNavigationClient';

const B2CErrorCodes = {
  USER_CANCEL: 'AADB2C90091',
};

/**
 * Lets every interactive redirect of a page load share the one started first; msal-react starts them as well
 * (MsalAuthenticationTemplate). A redirect started while another is under way fails with interaction_in_progress, and
 * MSAL's cleanup of that failure deletes the running redirect's cached request, so its response can't be redeemed.
 */
class SingleRedirectClientApplication extends PublicClientApplication {
  private pendingRedirect?: Promise<void>;

  constructor(configuration: Configuration) {
    super(configuration);

    // going back from the login page can restore this page from the back/forward cache;
    // MSAL then discards the abandoned redirect and a new one has to be possible
    this.addEventCallback((event) => {
      if (event.eventType === EventType.RESTORE_FROM_BFCACHE) {
        this.pendingRedirect = undefined;
      }
    });
  }

  loginRedirect(request?: RedirectRequest) {
    return this.redirectOnce(() => super.loginRedirect(request));
  }

  acquireTokenRedirect(request: RedirectRequest) {
    return this.redirectOnce(() => super.acquireTokenRedirect(request));
  }

  // a settled redirect stays pending: the page is navigating away and a new one would clear its cached request
  private redirectOnce(startRedirect: () => Promise<void>) {
    this.pendingRedirect ??= startRedirect().catch((error) => {
      this.pendingRedirect = undefined;
      throw error;
    });
    return this.pendingRedirect;
  }
}

let msalInstance: PublicClientApplication;
let navigationClient: CustomNavigationClient;
let initialization: Promise<AuthenticationResult | null> | undefined;

/** B2C reports its own error codes only inside the server's error description. */
const hasB2CErrorCode = (error: unknown, code: string) =>
  error instanceof AuthError && error.errorMessage.includes(code);

export const getInstance = () => {
  if (!msalInstance) {
    msalInstance = new SingleRedirectClientApplication(AuthConfig);
    msalInstance.setNavigationClient(getNavigationClient());

    msalInstance.addEventCallback((event) => {
      if (event.eventType !== EventType.ACQUIRE_TOKEN_FAILURE) {
        return;
      }

      // cancelling a sub-journey (sign-up, password reset) ends the whole flow, so start over at the sign-in page
      if (hasB2CErrorCode(event.error, B2CErrorCodes.USER_CANCEL)) {
        msalInstance.loginRedirect(getLoginRequest()).catch(console.error);
      }
    });
  }

  return msalInstance;
};

export const getNavigationClient = () => {
  navigationClient ??= new CustomNavigationClient();
  return navigationClient;
};

/**
 * Initializes MSAL and processes a pending redirect response once per page load.
 * Resolves with that response, or null if the page load did not complete an interactive flow.
 */
export const initializeMsal = () => {
  initialization ??= (async () => {
    const instance = getInstance();
    await instance.initialize();
    try {
      const result = await instance.handleRedirectPromise();
      if (result?.account) {
        instance.setActiveAccount(result.account);
      }
      return result;
    } finally {
      if (!instance.getActiveAccount()) {
        const [account] = instance.getAllAccounts();
        if (account) {
          instance.setActiveAccount(account);
        }
      }
    }
  })();

  return initialization;
};

export const getAccessToken = async () => {
  try {
    await initializeMsal();
  } catch (error) {
    // a failed redirect response is handled by the event callback; tokens cached before it are still usable
    console.warn(error);
  }

  const instance = getInstance();
  const account = instance.getActiveAccount();
  if (!account) {
    return null;
  }

  const request = { ...getLoginRequest(), account };

  try {
    const response = await instance.acquireTokenSilent(request);
    return response.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      await instance.acquireTokenRedirect(request).catch(console.error);
    } else {
      console.warn(error);
    }
    return null;
  }
};

type DecodedAccessToken = {
  email?: string;
  emails?: string[];
};

export const getDecodedAccessToken = async () => {
  const token = await getAccessToken();
  if (!token) {
    return null;
  }
  return jwtDecode<DecodedAccessToken>(token);
};
